import React, { useId, useState } from "react";
import { MedalBadge } from "./ProfileStats";
import { showMedal } from "../medalEvents";
import { t } from "../i18n";

// Renders a player with their equipped cosmetics. The same component everywhere
// (lobby, leaderboards, results) so the outfit is displayed identically on every
// screen. `player.cosmetics` comes from the server and is null for anonymous
// players.

// Avatar par défaut : les initiales sur un fond tiré du nom. Deux joueurs
// gardent ainsi deux couleurs distinctes, la même à chaque partie, et personne
// ne reçoit gratuitement un avatar qui se vend en boutique.
export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
// Teinte stable : même nom, même couleur, sur tous les écrans et tous les postes.
export function avatarHue(name) {
  let h = 0;
  for (const ch of String(name || "?")) h = (h * 31 + ch.codePointAt(0)) % 360;
  return h;
}
// `look` : un avatar de boutique aux couleurs d'un thème (styles/cosmetics.css).
// Les initiales restent celles du joueur, seul l'habillage change.
export function InitialsAvatar({ name, size = 40, className = "", look = "" }) {
  const hue = avatarHue(name);
  return (
    <span className={`pid-initials${look ? ` av-look av-look-${look}` : ""} ${className}`}
      style={{
        width: size, height: size, fontSize: Math.round(size * 0.4),
        ...(look ? {} : { background: `linear-gradient(140deg, hsl(${hue} 62% 46%), hsl(${(hue + 38) % 360} 60% 32%))` }),
      }}>
      <span className="pid-initials-txt">{initialsOf(name)}</span>
    </span>
  );
}

// L'image d'avatar à montrer : celle achetée en boutique l'emporte sur la photo
// envoyée, sauf si l'objet porté est justement le droit d'envoyer sa photo —
// son illustration n'est alors qu'une vignette de boutique.
export function avatarSrc(player) {
  const a = player?.cosmetics?.avatar;
  if (a?.look) return "";
  if (a?.image && !a.upload) return a.image;
  return player?.picture || "";
}

// Le cadre porté, en classes CSS : de quoi habiller une image ou des initiales
// hors du jeton (bouton de profil, aperçu du profil).
export function frameClass(player) {
  const look = player?.cosmetics?.frame?.look;
  return look ? ` fr-look fr-look-${look}` : "";
}

// L'habillage d'initiales porté, s'il y en a un.
export function avatarLook(player) {
  return player?.cosmetics?.avatar?.look || "";
}

export function PlayerAvatar({ player, size = 40, className = "" }) {
  const c = player?.cosmetics || {};
  const frame = c.frame;
  const style = { width: size, height: size };
  // `look` : un cadre de thème, dessiné dans styles/cosmetics.css
  if (frame) {
    if (frame.rainbow) style.background =
      "linear-gradient(#0000,#0000) padding-box, conic-gradient(#f87171,#f8cf01,#4ade80,#4dd7e8,#c084fc,#f87171) border-box";
    else if (frame.color) {
      style.borderColor = frame.color;
      if (frame.glow) style.boxShadow = `0 0 10px ${frame.color}`;
    }
  }
  return (
    <span className={`pid-avatar${frame ? " framed" : ""}${frame?.rainbow ? " rainbow" : ""}${frame?.look ? ` fr-look fr-look-${frame.look}` : ""} ${className}`} style={style}>
      {c.avatar?.emoji
        ? <span className="pid-avatar-emoji" style={{ fontSize: size * 0.6 }}>{c.avatar.emoji}</span>
        : avatarSrc(player)
        ? <img src={avatarSrc(player)} alt="" />
        : <InitialsAvatar name={player?.pseudo || player?.name} size={size} look={c.avatar?.look} />}
    </span>
  );
}

// Fond de bannière d'un jeton, d'une ligne de classement ou du bouton de profil.
// Pas de voile sur l'image : c'est le pseudo qui porte son propre fond flouté
// (layout.css, .has-banner). `tone` choisit l'encre et ce voile, `edge` la
// couleur de l'ombre sous le jeton. Les bannières à envoyer soi-même n'ont pas
// encore de fichier : elles n'habillent rien.
export function bannerStyle(player) {
  const b = player?.cosmetics?.banner;
  const bg = bannerBackground(b);
  if (!bg) return undefined;
  const light = b.tone === "light";
  return {
    ...bg,
    backgroundOrigin: "border-box",
    "--banner-ink": light ? "#0D1325" : "#FFFFFF",
    "--banner-scrim": light ? "rgba(255, 255, 255, .38)" : "rgba(13, 19, 37, .34)",
    "--banner-edge": b.edge || "rgba(0, 0, 0, .35)",
    ...(b.ring ? { "--banner-ring": b.ring } : {}),
  };
}

// Le fond seul d'une bannière, pour le jeton porté comme pour les vignettes.
// `css` : une bannière de thème, décrite en couches CSS complètes (tailles et
// répétitions comprises) ; `ring` : son liseré, posé à l'intérieur du jeton.
export function bannerBackground(b) {
  if (!b || b.upload) return undefined;
  if (b.css) return { background: b.css };
  const layer = b.gradient || (b.image && `url("${b.image}")`);
  if (!layer) return undefined;
  return { backgroundImage: layer, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" };
}

// Couronne de l'hôte : une image, remplaçable dans public/sprite/crown.svg.
// Elle remplace la pastille jaune d'autrefois — chacun garde désormais la
// parure qu'il s'est choisie, l'hôte se reconnaît à ce seul signe.
export function HostCrown({ size = 16, className = "" }) {
  return (
    <img src="/sprite/crown.svg" alt="" aria-hidden="true" draggable="false"
      className={`pid-crown ${className}`} style={{ width: size, height: size }} />
  );
}

// La flamme des séries, reprise de la maquette : trois couches (le corps,
// le cœur orange, la mèche jaune) dans une boîte de 31 × 31 posée à
// (235, 21) — on garde ce cadrage plutôt que de retoucher les tracés.
// Sans série en cours elle reste là, éteinte : la place ne bouge pas d'une
// ligne à l'autre. Le dégradé porte un identifiant unique par instance,
// sinon deux flammes sur la même page se voleraient leur définition.
const FLAME = {
  outer: "M261.854 41.0355C261.6 47.1212 256.587 51.9771 250.439 51.9771C244.128 51.9771 239.012 46.6889 239.012 40.5496C239.012 39.729 238.997 38.0891 240.227 35.3222C240.964 33.6663 241.426 32.626 241.686 31.6751C241.829 31.1525 242.108 30.3222 242.902 31.6751C243.37 32.4729 243.388 33.6202 243.388 33.6202C243.388 33.6202 245.13 32.2836 246.306 29.73C248.03 25.9866 246.654 23.7489 246.184 22.1928C246.022 21.6545 245.919 20.687 247.035 21.0987C248.172 21.5182 251.178 23.6222 252.749 25.8398C254.991 29.0049 255.788 32.0398 255.788 32.0398C255.788 32.0398 256.506 31.1487 256.761 30.2163C257.048 29.1634 257.053 28.1206 257.976 29.2439C258.855 30.3121 260.16 32.3196 260.894 34.2281C262.228 37.6939 261.854 41.0355 261.854 41.0355Z",
  mid: "M250.439 51.977C246.075 51.977 242.537 48.4393 242.537 44.0751C242.537 41.4418 243.598 39.843 245.807 37.675C247.221 36.2868 248.545 34.5818 249.107 33.4208C249.218 33.1923 249.47 32.0012 250.442 33.3958C250.951 34.1271 251.75 35.4278 252.263 36.5378C253.146 38.4517 253.357 40.3064 253.357 40.3064C253.357 40.3064 254.223 39.7963 254.816 38.4829C255.007 38.0596 255.393 36.4571 256.474 38.0594C257.267 39.2352 258.357 41.3493 258.341 44.0751C258.341 48.4393 254.803 51.977 250.439 51.977Z",
  core: "M250.561 43.3457C251.685 43.3457 251.685 45.4281 253.114 48.2084C254.065 50.0598 252.642 51.9771 250.561 51.9771C248.479 51.9771 247.4 50.2898 247.4 48.2084C247.4 46.1272 249.436 43.3457 250.561 43.3457Z",
};
export function StreakFlame({ streak = 0, size = 20, className = "" }) {
  const uid = useId().replace(/:/g, "");
  const hot = streak > 0;
  return (
    <span className={`pid-streak${hot ? " hot" : ""} ${className}`}>
      <svg viewBox="235 21 31 31" width={size} height={size} aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`flame${uid}`} x1="250.456" y1="51.9999" x2="250.456" y2="21.0228" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF4C0D" /><stop offset="1" stopColor="#FC9502" />
          </linearGradient>
        </defs>
        <path className="pid-flame-outer" fillRule="evenodd" clipRule="evenodd" d={FLAME.outer}
          fill={hot ? `url(#flame${uid})` : undefined} />
        <path className="pid-flame-mid" fillRule="evenodd" clipRule="evenodd" d={FLAME.mid} />
        <path className="pid-flame-core" fillRule="evenodd" clipRule="evenodd" d={FLAME.core} />
      </svg>
      {hot && <b className="pid-streak-count">{streak}</b>}
    </span>
  );
}

// L'étiquette d'un joueur en partie, d'après la maquette : trois pilules
// empilées. Celle du pseudo passe devant ; celle de la flamme dépasse derrière
// elle, puis celle des points derrière la flamme — chaque bord arrondi se
// pose sur le suivant. Sans série en cours, la pilule de la flamme disparaît.
// `afterName` : un signe collé au pseudo (a répondu) ; `beforeScore` : le gain
// de la question, à côté du total.
// Les médailles épinglées d'un joueur, chacune ouvrant sa fiche. Partagé par
// le tag de partie et la puce du salon.
export function PinnedMedals({ player, size = 34 }) {
  const medals = player?.cosmetics?.medals || [];
  if (!medals.length) return <span className="ptag-medals-none">{t("medals.playerMedalsNone")}</span>;
  return medals.map(m => (
    <button key={m.id} type="button" className="ptag-medal" title={m.name}
      onClick={e => { e.stopPropagation(); showMedal(m); }}>
      <MedalBadge medal={m} size={size} />
    </button>
  ));
}

export function PlayerTag({ player, pos, score = 0, avatarSize = 30, afterName, beforeScore, className = "" }) {
  const banner = player?.cosmetics?.banner;
  // Un clic déplie le tag vers le bas : les médailles épinglées du joueur,
  // chacune ouvrant sa fiche.
  const [open, setOpen] = useState(false);
  return (
    <div className={`ptag-wrap${open ? " open" : ""}`}>
      <div className={`ptag-row ${className}`} role="button" tabIndex={0} aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o => !o); } }}>
        {pos !== undefined && <span className="ptag-pos">{pos}</span>}
        <span className={`ptag ptag-id${banner ? " has-banner" : ""}`} style={bannerStyle(player)}>
          <PlayerAvatar player={player} size={avatarSize} />
          <PlayerName player={player} className="ptag-name" />
          {afterName}
        </span>
        {player?.streak > 0 && (
          <span className="ptag ptag-streak"><StreakFlame streak={player.streak} size={Math.round(avatarSize * .7)} /></span>
        )}
        <span className="ptag ptag-score">
          {beforeScore}
          <b>{String(score).padStart(2, "0")}</b>
        </span>
      </div>
      {open && <div className="ptag-medals"><PinnedMedals player={player} /></div>}
    </div>
  );
}

// `look` : un style de pseudo de thème (police, couleur et halo), dessiné dans
// styles/cosmetics.css. Les anciennes couleurs unies restent en style direct.
export function PlayerName({ player, className = "" }) {
  const c = player?.cosmetics || {};
  const look = c.color?.look;
  return (
    <span className={`pid-name-wrap ${className}`}>
      <span className={`pid-name${look ? ` pn-look pn-look-${look}` : ""}`}
        style={!look && c.color?.color ? { color: c.color.color } : undefined}>
        {player?.pseudo || player?.name}
      </span>
    </span>
  );
}

export default { PlayerAvatar, PlayerName, PlayerTag, HostCrown, StreakFlame, InitialsAvatar, bannerStyle, bannerBackground, avatarSrc, avatarLook };
